/**
 * This is based on vue-focus with some enhancements, particularly two-way binding
 * between the bound value and the focus state of the target element (value is automatically
 * updated on focus and blur events - no need for explicit @blur and @focus handlers).
 * NOTE: The binding expression should be the name of, or path to (e.g. 'data.nested'), a data
 * attribute or computed prop with setter (not a regular prop).
 *
 * Additionally, when the directive is applied to a Vue component, focus state is
 * transmitted through props and events, similar to v-model.
 * In the downstream direction, the directive value is bound to a child prop, the name
 * of which uses a convention defined below as "prop" in the schema but may be
 * overridden by the child component (similiar to "v-bind:value" in v-model).
 * In the upstream direction, the value in the binding expression is automatically
 * updated accordingly when the child component emits a blur or focus event (similar
 * to the "v-on:input" event in v-model).
 *
 * Modifiers:
 *
 * native - When directive is applied to a Vue component, override the special behavior
 *  and interact directly with the component's root element.
 *
 * preventScroll - Prevents the browser from scrolling to the focused element automatically.
 *
 * scrollTo - Automatically scrolls to the focused element smoothly (uses preventScroll
 *  to avoid native jerky browser scroll).
 */

import Vue from 'vue';
import { has, set } from 'lodash-es';

// Keep references to event handlers so they can be removed during disposal.
// A WeakMap is used to avoid a memory leak with the referenced elements.
const eventHandlers = new WeakMap();

export const defaultSchema = {
  // The name of the prop whose value should be bound to the value of the directive
  // when directive is used with a Vue component (similar to Vue's model.prop option)
  prop: 'setFocus',
  // When true, blur and focus events will be emitted
  bubble: true,
  // The name of the event to emit on blur (similar to Vue's model.event option)
  blurEvent: 'blur',
  // The name of the event to emit on focus (similar to Vue's model.event option)
  focusEvent: 'focus',
  // The name of the prop used to prevent scrolling on focus. Automatically set when
  // the preventScroll modifier is used anywhere in the parent v-focus chain.
  preventScrollProp: 'preventScrollOnFocus',
  // The name of the prop used to automatically scroll to the focused element. Automatically
  // set when the scrollTo modifier is used anywhere in the parent v-focus chain.
  scrollToProp: 'scrollToOnFocus'
};

function getSchema(vm) {
  return { ...defaultSchema, ...vm.$options.focusSchema || {} };
}

function setProp(vm, propName, value) {
  // We have to mutate the prop directly since we're not using v-bind,
  // so temporarily silence the usual Vue warning
  const { silent } = Vue.config;
  Vue.config.silent = true; // silence the Vue warning

  try {
    set(vm, propName, value); // mutate the prop
  } finally {
    Vue.config.silent = silent; // restore the original Vue setting
  }
}

/**
 * Get the parent component instance from the vnode. Supports standard and functional components.
 *
 * @param {VNode} vnode The Vue VNode.
 */
const getParentComponent = ({ context }) => context._original // Support functional components
  || context;

/**
 * When the directive is bound to a native HTML element (or bound to a Vue component with
 * the "native" modifier), the element is focused or blurred when the value is either
 * true or false, respectively.
 *
 * @param {HTMLElement} el The element the directive is bound to.
 * @param {Object} binding The Vue binding.
 * @param {VNode} vnode The Vue VNode.
 */
function handleNativeValueChange(el, { modifiers, value }, vnode) {
  if (value) {
    const parent = getParentComponent(vnode);
    const { preventScrollProp, scrollToProp } = getSchema(parent);
    const preventScroll = modifiers.preventScroll || parent[preventScrollProp];
    const scrollTo = modifiers.scrollTo || parent[scrollToProp];

    if (document.activeElement === el) return;

    if (scrollTo) el.scrollIntoView({ behavior: 'smooth' });

    // scrollTo overrides preventScroll, since preventScroll must be passed to focus()
    // for smooth scrolling.
    el.focus({ preventScroll: preventScroll || scrollTo });
  } else {
    el.blur();
  }
}

/**
 * When the directive is bound to a Vue component, the value is passed down to the
 * component's prop. Also syncs the preventScroll and scrollTo props with the values of
 * the preventScroll and scrollTo modifiers or the parent component's preventScrollProp
 * or scrollToProp, respectively.
 *
 * @param {Object} binding The Vue binding.
 * @param {VNode} vnode The Vue VNode.
 */
function handleComponentValueChange({ modifiers, value }, vnode) {
  const { componentInstance } = vnode;
  const { preventScrollProp, scrollToProp } = getSchema(componentInstance);
  const parent = getParentComponent(vnode);

  if (modifiers.preventScroll || parent[preventScrollProp]) {
    setProp(componentInstance, preventScrollProp, true);
  }
  if (modifiers.scrollTo || parent[scrollToProp]) {
    setProp(componentInstance, scrollToProp, true);
  }

  const { prop } = getSchema(componentInstance);
  setProp(componentInstance, prop, value);
}

export default {
  /**
   * Called when the bound element has been inserted into its parent node.
   *
   * @param {HTMLElement} el The element the directive is bound to.
   * @param {Object} binding The Vue binding.
   * @param {VNode} vnode The Vue VNode.
   */
  inserted(el, binding, vnode) {
    const { expression, modifiers } = binding;
    const { componentInstance } = vnode;

    const parent = getParentComponent(vnode);

    if (!has(parent, expression)) {
      if (!Vue.config.silent) {
        console.warn(`${expression} is not defined on the component.`); // eslint-disable-line no-console
      }
      return;
    }

    const blurHandler = () => {
      setProp(parent, expression, false);
    };

    const focusHandler = () => {
      setProp(parent, expression, true);
    };

    if (!componentInstance || modifiers.native) {
      handleNativeValueChange(el, binding, vnode);

      el.addEventListener('blur', blurHandler);
      el.addEventListener('focus', focusHandler);
    } else {
      handleComponentValueChange(binding, vnode);

      const { blurEvent, focusEvent } = getSchema(componentInstance);

      componentInstance.$on(blurEvent, blurHandler);
      componentInstance.$on(focusEvent, focusHandler);
    }

    eventHandlers.set(el, {
      blur: blurHandler,
      focus: focusHandler
    });
  },

  /**
   * Called after the containing component’s VNode and the VNodes of its children have updated.
   *
   * @param {HTMLElement} el The element the directive is bound to.
   * @param {Object} binding The Vue binding.
   * @param {VNode} vnode The Vue VNode.
   */
  componentUpdated(el, binding, vnode) {
    const { modifiers, value, oldValue } = binding;

    if (Boolean(value) === Boolean(oldValue)) {
      return;
    }

    if (!vnode.componentInstance || modifiers.native) {
      handleNativeValueChange(el, binding, vnode);
    } else {
      handleComponentValueChange(binding, vnode);
    }
  },

  /**
   * Called only once, when the directive is unbound from the element.
   *
   * @param {HTMLElement} el The element the directive is bound to.
   * @param {Object} binding The Vue binding.
   * @param {VNode} vnode The Vue VNode.
   */
  unbind(el, { modifiers }, { componentInstance }) {
    const { blur, focus } = eventHandlers.get(el);

    if (!componentInstance || modifiers.native) {
      el.removeEventListener('blur', blur);
      el.removeEventListener('focus', focus);
    } else {
      const { blurEvent, focusEvent } = getSchema(componentInstance);

      componentInstance.$off(blurEvent, blur);
      componentInstance.$off(focusEvent, focus);
    }
  }
};
